import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import moment from "moment";

const categoryColumns = [
    { key: "image", label: "Image", width: "w-20", stickyLeft: 0 },
    { key: "name", label: "Name", width: "w-56", stickyLeft: 0 }, // overridden at runtime via measured image width
    { key: "description", label: "Description", width: "w-96" },
    { key: "sequence", label: "Sequence", width: "w-32" },
    { key: "is_active", label: "Status", width: "w-32" },
    { key: "id", label: "ID", width: "w-96" },
    { key: "created_at", label: "Created", width: "w-48" },
    { key: "updated_at", label: "Updated", width: "w-48" },
    { key: "action", label: "", width: "w-16" },
];

const W_TO_PX = {
    "w-12": 48, "w-16": 64, "w-20": 80, "w-24": 96, "w-28": 112, "w-32": 128,
    "w-36": 144, "w-40": 160, "w-44": 176, "w-48": 192, "w-56": 224,
    "w-64": 256, "w-72": 288, "w-80": 320, "w-96": 384,
};
const widthPx = (cls) => W_TO_PX[cls] ?? 128;

const HEADER_BG = "bg-charleston-green";
const ROW_BG = "bg-eerie-black group-hover:bg-charleston-green";

const HeaderCell = ({ children, width, stickyLeft }) => {
    const isSticky = stickyLeft != null;
    return (
        <th
            scope="col"
            style={isSticky ? { left: stickyLeft } : undefined}
            className={`${width} sticky top-0 ${isSticky ? "z-30" : "z-10"} ${HEADER_BG} border-b border-r border-zinc-700 px-3 py-2.5 text-left text-xs font-medium text-zinc-400 uppercase tracking-wide whitespace-nowrap`}
        >
            {children}
        </th>
    );
};

const BodyCell = ({ children, width, className = "", stickyLeft }) => {
    const isSticky = stickyLeft != null;
    return (
        <td
            style={isSticky ? { left: stickyLeft } : undefined}
            className={`${width} border-b border-r border-zinc-800 px-3 py-2 text-sm text-zinc-200 align-top ${ROW_BG} ${
                isSticky ? "sticky z-20" : ""
            } ${className}`}
        >
            {children}
        </td>
    );
};

const Empty = () => <span className="text-zinc-600">—</span>;

const formatDate = (value) => {
    if (!value) return null;
    const m = moment(value);
    return m.isValid() ? m.format("DD MMM YYYY, HH:mm") : value;
};

const Status = ({ value }) => {
    const colors = value
        ? "bg-green-950 text-green-500 border-green-500"
        : "bg-red-950 text-red-500 border-red-500";
    return (
        <span className={`px-2.5 py-1 text-[10px] rounded-full border ${colors}`}>
            {value ? "ACTIVE" : "INACTIVE"}
        </span>
    );
};

const Thumb = ({ src, alt }) =>
    src ? (
        <img
            src={src}
            alt={alt || ""}
            title={src}
            className="h-8 w-8 rounded object-cover border border-zinc-700"
        />
    ) : (
        <Empty />
    );

const Text = ({ value, className = "" }) =>
    value == null || value === "" ? <Empty /> : <span className={className}>{value}</span>;

const CLAMP_2_LINES_STYLE = {
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: 2,
    overflow: "hidden",
};

const ClampCell = ({ value, width, breakStyle = "break-all", className = "" }) => {
    if (value == null || value === "") return <Empty />;
    return (
        <div className={width}>
            <div
                style={{ ...CLAMP_2_LINES_STYLE, wordBreak: breakStyle }}
                className={className}
                title={typeof value === "string" ? value : String(value)}
            >
                {value}
            </div>
        </div>
    );
};

const EyeIcon = () => (
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
);

const Table = ({ columns, children }) => (
    <table className="border-separate table-fixed" style={{ borderSpacing: 0 }}>
        <colgroup>
            {columns.map((col) => (
                <col key={col.key} style={{ width: widthPx(col.width) }} />
            ))}
        </colgroup>
        <thead>
            <tr>
                {columns.map((col) => (
                    <HeaderCell key={col.key} width={col.width} stickyLeft={col.stickyLeft}>
                        {col.label}
                    </HeaderCell>
                ))}
            </tr>
        </thead>
        <tbody>{children}</tbody>
    </table>
);

const CategoryRow = ({ category, onView, nameStickyLeft }) => (
    <tr className="group bg-eerie-black hover:bg-charleston-green transition-colors">
        <BodyCell width="w-20" stickyLeft={0}><Thumb src={category.image} alt={category.name} /></BodyCell>
        <BodyCell width="w-56" stickyLeft={nameStickyLeft} className="font-medium text-white whitespace-nowrap"><Text value={category.name} /></BodyCell>
        <BodyCell width="w-96" className="text-zinc-300 text-sm">
            <ClampCell value={category.description} width="w-96" breakStyle="break-word" />
        </BodyCell>
        <BodyCell width="w-32"><Text value={category.sequence} /></BodyCell>
        <BodyCell width="w-32"><Status value={category.is_active} /></BodyCell>
        <BodyCell width="w-96" className="text-zinc-500 font-mono text-sm">
            <ClampCell value={category.id} width="w-96" breakStyle="break-all" />
        </BodyCell>
        <BodyCell width="w-48" className="whitespace-nowrap text-zinc-400 text-sm">
            {formatDate(category.created_at) || <Empty />}
        </BodyCell>
        <BodyCell width="w-48" className="whitespace-nowrap text-zinc-400 text-sm">
            {formatDate(category.updated_at) || <Empty />}
        </BodyCell>
        <BodyCell width="w-16">
            <button
                onClick={() => onView(category)}
                className="p-2 text-zinc-500 hover:text-white border border-zinc-700 rounded-full transition-colors"
                aria-label="View category"
            >
                <EyeIcon />
            </button>
        </BodyCell>
    </tr>
);

const CategoryList = ({ data, onView }) => {
    const wrapperRef = useRef(null);
    const [nameStickyLeft, setNameStickyLeft] = useState(0);

    const measure = useCallback(() => {
        const firstHeader = wrapperRef.current?.querySelector("thead th:first-child");
        if (firstHeader) {
            setNameStickyLeft(firstHeader.getBoundingClientRect().width);
        }
    }, []);

    useLayoutEffect(() => {
        measure();
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, [measure, data]);

    const columns = useMemo(
        () =>
            categoryColumns.map((c) =>
                c.key === "name" ? { ...c, stickyLeft: nameStickyLeft } : c
            ),
        [nameStickyLeft]
    );

    return (
        <div className="border border-zinc-700 rounded-lg overflow-hidden">
            <div ref={wrapperRef} className="overflow-auto max-h-[calc(100vh-260px)]">
                <Table columns={columns}>
                    {data.map((category) => (
                        <CategoryRow key={category.id} category={category} onView={onView} nameStickyLeft={nameStickyLeft} />
                    ))}
                </Table>
            </div>
        </div>
    );
};

export default CategoryList;
